import React, { useEffect, useMemo, useState } from 'react';
import ReactDOM from 'react-dom';
import styled from 'styled-components';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import ContentCopyOutlinedIcon from '@mui/icons-material/ContentCopyOutlined';
import DirectionsOutlinedIcon from '@mui/icons-material/DirectionsOutlined';
import MapOutlinedIcon from '@mui/icons-material/MapOutlined';

import { toCoordinate } from '../../Utils/geo';
import { useI18n } from '../../i18n/I18nProvider';

export const buildNavigationLinks = ({ location, address = '' }) => {
  const coordinate = toCoordinate(location);
  const destination = coordinate
    ? `${coordinate.latitude},${coordinate.longitude}`
    : String(address || '').trim();
  const encoded = encodeURIComponent(destination);
  const encodedAddress = encodeURIComponent(address || destination);

  return {
    google: `https://www.google.com/maps/dir/?api=1&destination=${encoded}&travelmode=driving&utm_source=lepetitboulot`,
    apple: `https://maps.apple.com/?daddr=${encoded}&dirflg=d`,
    waze: coordinate
      ? `https://www.waze.com/ul?ll=${coordinate.latitude}%2C${coordinate.longitude}&navigate=yes&utm_source=lepetitboulot`
      : `https://www.waze.com/ul?q=${encodedAddress}&navigate=yes&utm_source=lepetitboulot`,
    bing: coordinate
      ? `https://www.bing.com/maps?rtp=~pos.${coordinate.latitude}_${coordinate.longitude}_${encodedAddress}`
      : `https://www.bing.com/maps?q=${encodedAddress}`,
  };
};

const NavigationMenu = ({ location, address, compact = false, disabled = false }) => {
  const { t } = useI18n();
  const [opened, setOpened] = useState(false);
  const [copied, setCopied] = useState(false);
  const links = useMemo(() => buildNavigationLinks({ location, address }), [address, location]);

  useEffect(() => {
    if (!opened) return undefined;
    const onKeyDown = (event) => {
      if (event.key === 'Escape') setOpened(false);
    };
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    window.addEventListener('keydown', onKeyDown);
    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener('keydown', onKeyDown);
    };
  }, [opened]);

  const copyAddress = async () => {
    try {
      await navigator.clipboard?.writeText(address || '');
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1800);
    } catch (_) {
      setCopied(false);
    }
  };

  const modal = opened && !disabled && typeof document !== 'undefined'
    ? ReactDOM.createPortal(
      <Backdrop role="presentation" onMouseDown={() => setOpened(false)}>
        <Sheet role="dialog" aria-modal="true" aria-labelledby="navigation-title" onMouseDown={(event) => event.stopPropagation()}>
          <SheetHeader>
            <div>
              <SheetEyebrow>{t('location.itinerary')}</SheetEyebrow>
              <SheetTitle id="navigation-title">{t('location.chooseApp')}</SheetTitle>
              {address && <SheetAddress>{address}</SheetAddress>}
            </div>
            <CloseButton type="button" onClick={() => setOpened(false)} aria-label={t('common.close')}>
              <CloseRoundedIcon />
            </CloseButton>
          </SheetHeader>

          <ProviderGrid>
            <ProviderLink href={links.google} target="_blank" rel="noopener noreferrer" onClick={() => setOpened(false)}>
              <ProviderIcon><MapOutlinedIcon /></ProviderIcon>
              <span><strong>Google Maps</strong><small>{t('location.navigationTraffic')}</small></span>
            </ProviderLink>
            <ProviderLink href={links.apple} target="_blank" rel="noopener noreferrer" onClick={() => setOpened(false)}>
              <ProviderIcon><MapOutlinedIcon /></ProviderIcon>
              <span><strong>Apple Maps</strong><small>{t('location.appleDevices')}</small></span>
            </ProviderLink>
            <ProviderLink href={links.waze} target="_blank" rel="noopener noreferrer" onClick={() => setOpened(false)}>
              <ProviderIcon><MapOutlinedIcon /></ProviderIcon>
              <span><strong>Waze</strong><small>{t('location.communityNavigation')}</small></span>
            </ProviderLink>
            <ProviderLink href={links.bing} target="_blank" rel="noopener noreferrer" onClick={() => setOpened(false)}>
              <ProviderIcon><MapOutlinedIcon /></ProviderIcon>
              <span><strong>Bing Maps</strong><small>{t('location.webAlternative')}</small></span>
            </ProviderLink>
          </ProviderGrid>

          {address && (
            <CopyButton type="button" onClick={copyAddress}>
              <ContentCopyOutlinedIcon /> {copied ? t('location.copied') : t('location.copyAddress')}
            </CopyButton>
          )}
        </Sheet>
      </Backdrop>,
      document.body,
    )
    : null;

  return (
    <Wrapper>
      <Trigger type="button" onClick={() => setOpened(true)} disabled={disabled} $compact={compact}>
        <DirectionsOutlinedIcon /> {disabled ? t('location.unavailable') : t('location.openItinerary')}
      </Trigger>
      {modal}
    </Wrapper>
  );
};

const Wrapper = styled.div`
  width: fit-content;
  max-width: 100%;
`;

const Trigger = styled.button`
  min-height: ${({ $compact }) => ($compact ? '2.55rem' : '2.9rem')};
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: .45rem;
  max-width: 100%;
  padding: ${({ $compact }) => ($compact ? '.55rem .75rem' : '.7rem 1rem')};
  border: 1px solid #b9ceda;
  border-radius: 999px;
  background: #f3f8fb;
  color: #111111;
  font: inherit;
  font-size: .8rem;
  font-weight: 850;
  cursor: pointer;
  white-space: nowrap;

  &:hover:not(:disabled) { background: #f3eee6; }
  &:disabled { opacity: .55; cursor: not-allowed; }
  svg { flex: 0 0 auto; font-size: 1.08rem; }
`;

const Backdrop = styled.div`
  position: fixed;
  inset: 0;
  z-index: 5000;
  display: grid;
  place-items: center;
  padding: 1rem;
  background: rgba(8, 28, 44, .52);
  backdrop-filter: blur(4px);
`;

const Sheet = styled.div`
  width: min(34rem, 100%);
  max-height: min(90vh, 44rem);
  overflow-y: auto;
  display: grid;
  gap: 1rem;
  padding: 1rem;
  border: 1px solid #e7dfd2;
  border-radius: 1.25rem;
  background: #fff;
  box-shadow: 0 1.4rem 4rem rgba(8, 32, 51, .28);

  @media (max-width: 36rem) {
    align-self: end;
    width: calc(100% + 2rem);
    margin: 0 -1rem -1rem;
    border-radius: 1.35rem 1.35rem 0 0;
    padding: 1.1rem 1rem calc(1rem + env(safe-area-inset-bottom));
  }
`;

const SheetHeader = styled.header`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1rem;
`;

const SheetEyebrow = styled.span`
  display: block;
  margin-bottom: .2rem;
  color: #a88a5d;
  font-size: .68rem;
  font-weight: 900;
  letter-spacing: .09em;
  text-transform: uppercase;
`;

const SheetTitle = styled.h3`
  margin: 0;
  color: #111111;
  font-size: 1.2rem;
`;

const SheetAddress = styled.p`
  margin: .35rem 0 0;
  color: #617584;
  font-size: .82rem;
  line-height: 1.45;
`;

const CloseButton = styled.button`
  flex: 0 0 auto;
  width: 2.4rem;
  height: 2.4rem;
  display: grid;
  place-items: center;
  border: 0;
  border-radius: .75rem;
  background: #f1ece5;
  color: #29495d;
  cursor: pointer;
`;

const ProviderGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: .65rem;

  @media (max-width: 34rem) { grid-template-columns: 1fr; }
`;

const ProviderLink = styled.a`
  min-width: 0;
  display: flex;
  align-items: center;
  gap: .7rem;
  padding: .8rem;
  border: 1px solid #e7dfd2;
  border-radius: .9rem;
  background: #f8fbfc;
  color: #111111;
  text-decoration: none;
  transition: transform 150ms ease, border-color 150ms ease, background 150ms ease;

  &:hover {
    transform: translateY(-1px);
    border-color: #9fc3d7;
    background: #f7f3ed;
  }

  span { min-width: 0; display: grid; gap: .1rem; }
  strong { font-size: .85rem; }
  small { color: #6d7f8b; font-size: .7rem; }
`;

const ProviderIcon = styled.span`
  flex: 0 0 auto;
  width: 2.3rem;
  height: 2.3rem;
  display: grid;
  place-items: center;
  border-radius: .7rem;
  background: #efe8dc;
  color: #111111;
  svg { font-size: 1.15rem; }
`;

const CopyButton = styled.button`
  min-height: 2.7rem;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: .5rem;
  padding: .65rem .8rem;
  border: 1px solid #e7dfd2;
  border-radius: .85rem;
  background: #fff;
  color: #111111;
  font: inherit;
  font-size: .8rem;
  font-weight: 800;
  cursor: pointer;
  &:hover { background: #f5f9fb; }
  svg { font-size: 1rem; }
`;

export default NavigationMenu;
